// Formulario crear/editar plato_template con BOM (lista ingredientes + qty + unidad).
// El BOM se mantiene client-side por ahora (T8 backend no expone receta_ingrediente).
import { useEffect, useMemo, useState } from 'react'
import type {
  BomItem,
  IngredienteResponse,
  PlatoTemplateCreateBody,
  PlatoTemplateResponse,
  PlatoTemplateUpdateBody,
  UnidadMedidaResponse,
} from '../../services/nutricionista'
import { nutricionistaApi } from '../../services/nutricionista'
import { ModalShell } from '../ui/ModalShell'

interface PlatoTemplateFormProps {
  initial?: PlatoTemplateResponse | null
  ingredientesDisponibles: IngredienteResponse[]
  onSubmit: (
    body: PlatoTemplateCreateBody | PlatoTemplateUpdateBody,
    bom: BomItem[],
  ) => Promise<void> | void
  onCancel: () => void
}

// S8: dropdown dinamico desde /catalogo/unidades-medida (no hardcoded).
// El array UNIDADES legacy se conserva como fallback si la API falla.
const UNIDADES_FALLBACK = ['g', 'ml', 'oz', 'unid', 'taza', 'cda']

export default function PlatoTemplateForm({
  initial,
  ingredientesDisponibles,
  onSubmit,
  onCancel,
}: PlatoTemplateFormProps) {
  const editing = !!initial
  // S8: codigo es read-only (auto-generado PLA{N:03d}); solo lo mostramos en
  // modo edicion. NO se exporta setter.
  const [codigo] = useState(initial?.codigo ?? '')
  const [nombre, setNombre] = useState(initial?.nombre_compuesto ?? '')
  const [descripcion, setDescripcion] = useState(initial?.descripcion ?? '')
  const [activo, setActivo] = useState(initial?.activo ?? true)
  const [bom, setBom] = useState<BomItem[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unidades, setUnidades] = useState<UnidadMedidaResponse[]>([])

  // Cargar catalogo de unidades de medida desde el backend (Sprint 8).
  useEffect(() => {
    let cancelled = false
    nutricionistaApi
      .listarUnidadesMedida()
      .then((data) => {
        if (!cancelled) setUnidades(data)
      })
      .catch(() => {
        // Si falla la API, dropdowns usan fallback hardcoded en render.
        if (!cancelled) setUnidades([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const ingredientesAprobados = useMemo(
    () =>
      ingredientesDisponibles.filter(
        (i) => String(i.estado_workflow).toUpperCase() === 'APROBADO',
      ),
    [ingredientesDisponibles],
  )

  function addBomRow() {
    if (ingredientesAprobados.length === 0) return
    // S8: NO seleccionar primer ingrediente por default — usuario debe
    // elegir explicitamente (evita confusion de filas con mismo ingrediente).
    setBom((prev) => [
      ...prev,
      {
        ingrediente_id: 0,
        ingrediente_codigo: undefined,
        ingrediente_nombre: undefined,
        cantidad: 0,
        unidad: 'g',
      },
    ])
  }

  function updateBomRow(idx: number, patch: Partial<BomItem>) {
    setBom((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)))
  }

  function removeBomRow(idx: number) {
    setBom((prev) => prev.filter((_, i) => i !== idx))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    // S8: codigo se auto-genera en backend (PLA{N:03d}). Frontend no valida.
    if (!nombre.trim()) {
      setError('El nombre del plato es obligatorio')
      return
    }
    const bomSinIngrediente = bom.findIndex((b) => !b.ingrediente_id)
    if (bomSinIngrediente !== -1) {
      setError(`Fila ${bomSinIngrediente + 1} del BOM: seleccioná un ingrediente`)
      return
    }
    // S8 T25: cada item del BOM debe tener cantidad > 0; en creacion ademas
    // exigimos al menos 1 fila (un plato sin ingredientes no tiene sentido).
    const bomSinCantidad = bom.findIndex((b) => !b.cantidad || b.cantidad <= 0)
    if (bomSinCantidad !== -1) {
      setError(`Fila ${bomSinCantidad + 1} del BOM: la cantidad debe ser mayor a 0`)
      return
    }
    if (!editing && bom.length === 0) {
      setError('Agregá al menos un ingrediente al BOM antes de crear el plato')
      return
    }
    setSubmitting(true)
    try {
      if (editing) {
        const upd: PlatoTemplateUpdateBody = {
          nombre_compuesto: nombre.trim(),
          descripcion: descripcion.trim() || null,
          activo,
          // Sprint 8 final: incluir BOM al editar para que el backend lo persista
          bom: bom.map((b) => ({
            ingrediente_id: b.ingrediente_id,
            cantidad: b.cantidad,
            unidad: b.unidad,
          })),
        }
        await onSubmit(upd, bom)
      } else {
        const create: PlatoTemplateCreateBody = {
          codigo: 'AUTO', // S8: ignorado por backend, se auto-genera PLA{N:03d}
          nombre_compuesto: nombre.trim().toUpperCase(),
          descripcion: descripcion.trim() || null,
          activo,
          // S8 T25: persistir BOM junto con el plato
          bom: bom.map((b) => ({
            ingrediente_id: b.ingrediente_id,
            cantidad: b.cantidad,
            unidad: b.unidad,
          })),
        }
        await onSubmit(create, bom)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title={editing ? 'Editar plato' : 'Nuevo plato'}
      onClose={onCancel}
      closeDisabled={submitting}
      maxWidth={640}
    >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold">Código</span>
              {editing ? (
                <input
                  type="text"
                  value={codigo}
                  disabled
                  className="mt-1 w-full border rounded px-2 py-1 text-sm uppercase bg-gray-100"
                  aria-label="Codigo del plato"
                />
              ) : (
                <div className="mt-1 w-full border rounded px-2 py-1 text-sm bg-gray-50 text-gray-500 italic">
                  Se asigna automáticamente (PLAnnn)
                </div>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Nombre compuesto</span>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                disabled={submitting}
                maxLength={250}
                aria-label="Nombre del plato"
                className="mt-1 w-full border rounded px-2 py-1 text-sm uppercase"
                style={{ textTransform: 'uppercase' }}
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-semibold">Descripcion</span>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              disabled={submitting}
              rows={2}
              aria-label="Descripcion"
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
            />
          </label>

          <fieldset className="border rounded p-3">
            <legend className="text-sm font-semibold px-1">
              BOM — Ingredientes ({bom.length})
            </legend>
            {ingredientesAprobados.length === 0 && (
              <p className="text-xs text-amber-700 mb-2">
                No hay ingredientes APROBADOS. Aprueba ingredientes antes de armar BOM.
              </p>
            )}
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-gray-600">
                <tr>
                  <th className="pb-1">Ingrediente</th>
                  <th className="pb-1 w-24">Cantidad</th>
                  <th className="pb-1 w-24">Unidad</th>
                  <th className="pb-1 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {bom.map((row, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="py-1">
                      <select
                        value={row.ingrediente_id}
                        onChange={(e) => {
                          const id = Number(e.target.value)
                          const ing = ingredientesAprobados.find((i) => i.id === id)
                          updateBomRow(idx, {
                            ingrediente_id: id,
                            ingrediente_codigo: ing?.codigo,
                            ingrediente_nombre: ing?.nombre,
                            // Sincronizar unidad default del ingrediente seleccionado
                            ...(ing?.unidad_default ? { unidad: ing.unidad_default } : {}),
                          })
                        }}
                        aria-label={`Ingrediente fila ${idx + 1}`}
                        className="w-full border rounded px-1 py-0.5 text-sm"
                      >
                        <option value={0} disabled>
                          Selecciona ingrediente...
                        </option>
                        {ingredientesAprobados.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.codigo} — {i.nombre}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        // S8: mostrar string vacio cuando cantidad=0 para que la
                        // nutricionista NO vea "0" como prefijo al escribir.
                        value={row.cantidad === 0 ? '' : row.cantidad}
                        onChange={(e) => {
                          const v = e.target.value
                          updateBomRow(idx, {
                            cantidad: v === '' ? 0 : Number(v),
                          })
                        }}
                        placeholder="0"
                        aria-label={`Cantidad fila ${idx + 1}`}
                        className="w-full border rounded px-1 py-0.5 text-sm"
                      />
                    </td>
                    <td className="py-1">
                      <select
                        value={row.unidad}
                        onChange={(e) => updateBomRow(idx, { unidad: e.target.value })}
                        aria-label={`Unidad fila ${idx + 1}`}
                        className="w-full border rounded px-1 py-0.5 text-sm"
                      >
                        {unidades.length > 0
                          ? unidades.map((u) => (
                              <option key={u.id} value={u.codigo}>
                                {u.abreviatura} ({u.nombre})
                              </option>
                            ))
                          : UNIDADES_FALLBACK.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                      </select>
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        onClick={() => removeBomRow(idx)}
                        aria-label={`Eliminar fila ${idx + 1}`}
                        className="text-red-600 text-xs hover:underline"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={addBomRow}
              disabled={ingredientesAprobados.length === 0}
              className="mt-2 text-sm text-avante-primary underline disabled:opacity-50"
            >
              + Agregar ingrediente
            </button>
          </fieldset>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              aria-label="Activo"
            />
            Activo
          </label>

          {error && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 border py-2 rounded text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-avante-primary text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear plato'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
}
